import React, { createContext, useCallback, useContext, useState, ReactNode, useEffect, useMemo } from 'react';
import { Specialist } from 'src/features/chat/types';
import { useSpecialistsManagement } from 'src/hooks/useSpecialistsManagement';

const MAX_DISPLAY_SPECIALISTS = 4;

type SpecialistPayload = Pick<Specialist, 'name' | 'colour' | 'prompt_parts' | 'knowledge_base' | 'tool_set'>;

interface SpecialistContextType {
  selectedSpecialist: Specialist | null;
  setSelectedSpecialist: (specialist: Specialist | null) => void;
  specialists: Specialist[];
  setSpecialists: (specialists: Specialist[]) => void;
  specialistsLoading: boolean;
  fetchSpecialists: () => Promise<void>;
  addSpecialist: (specialist: SpecialistPayload) => Promise<Specialist | null>;
  updateSpecialist: (id: string, specialist: Partial<SpecialistPayload>) => Promise<Specialist | null>;
  deleteSpecialist: (id: string) => Promise<boolean>;
  openManageModal: (() => void) | null;
  setOpenManageModal: (callback: () => void) => void;
  /** Open the Specialists modal with a specific specialist's detail
   * view pre-selected. Wired by ``ChatLayout`` — null when the modal
   * bridge hasn't mounted yet (e.g. outside the chat page). */
  openSpecialistById: ((specialistId: string) => void) | null;
  setOpenSpecialistById: (callback: (specialistId: string) => void) => void;
  displayedSpecialists: Specialist[];
}

const SpecialistContext = createContext<SpecialistContextType | undefined>(undefined);

interface SpecialistProviderProps {
  children: ReactNode;
  specialistId?: string | null;
  setSpecialistId?: (specialistId: string | null) => void;
}

export const SpecialistProvider: React.FC<SpecialistProviderProps> = ({ children, specialistId, setSpecialistId }) => {
  const [selectedSpecialist, setSelectedSpecialistState] = useState<Specialist | null>(null);
  const [openManageModal, setOpenManageModal] = useState<(() => void) | null>(null);
  const [openSpecialistById, setOpenSpecialistById] = useState<((id: string) => void) | null>(null);

  const {
    specialists,
    setSpecialists,
    loading: specialistsLoading,
    fetchSpecialists,
    addSpecialist,
    updateSpecialist,
    deleteSpecialist
  } = useSpecialistsManagement();

  // Fetch specialists on mount
  useEffect(() => {
    fetchSpecialists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync selectedSpecialist with URL state (specialistId)
  useEffect(() => {
    if (specialistId && specialists.length > 0) {
      const specialist = specialists.find((s) => s.id === specialistId);

      // Only set the specialist if it exists and hasn't been deleted
      if (specialist && !specialist.deleted_at) {
        setSelectedSpecialistState(specialist);
      } else {
        // Specialist not found or deleted - clear from state
        setSelectedSpecialistState(null);
      }
    } else if (!specialistId) {
      setSelectedSpecialistState(null);
    }
  }, [specialistId, specialists]);

  // Wrapper function to update both local state and URL
  const setSelectedSpecialist = useCallback(
    (specialist: Specialist | null) => {
      setSelectedSpecialistState(specialist);
      if (setSpecialistId) {
        setSpecialistId(specialist?.id || null);
      }
    },
    [setSpecialistId]
  );

  // Filter and sort specialists for display:
  // - Max 5 specialists
  // - Sorted by most recently used (using last_used_at from database)
  // - Public specialists only shown if:
  //   - They were created by the user (is_owner), OR
  //   - They have been used before (last_used_at is set)
  const displayedSpecialists = useMemo(() => {
    const nonDeleted = specialists.filter((s) => !s.deleted_at);

    // Sort by most recently used (last_used_at), then alphabetically
    const sorted = nonDeleted.sort((a, b) => {
      const aUsed = a.last_used_at ? new Date(a.last_used_at).getTime() : 0;
      const bUsed = b.last_used_at ? new Date(b.last_used_at).getTime() : 0;

      // Both have been used - sort by most recent first
      if (aUsed && bUsed) {
        return bUsed - aUsed; // Descending (most recent first)
      }
      // Only a has been used - a comes first
      if (aUsed) return -1;
      // Only b has been used - b comes first
      if (bUsed) return 1;
      // Neither has been used - sort alphabetically by name
      return a.name.localeCompare(b.name);
    });

    // Limit to max 5
    return sorted.slice(0, MAX_DISPLAY_SPECIALISTS);
  }, [specialists]);

  const value = useMemo(
    () => ({
      selectedSpecialist,
      setSelectedSpecialist,
      specialists,
      setSpecialists,
      specialistsLoading,
      fetchSpecialists,
      addSpecialist,
      updateSpecialist,
      deleteSpecialist,
      openManageModal,
      setOpenManageModal,
      openSpecialistById,
      setOpenSpecialistById,
      displayedSpecialists
    }),
    [
      selectedSpecialist,
      setSelectedSpecialist,
      specialists,
      setSpecialists,
      specialistsLoading,
      fetchSpecialists,
      addSpecialist,
      updateSpecialist,
      deleteSpecialist,
      openManageModal,
      setOpenManageModal,
      openSpecialistById,
      setOpenSpecialistById,
      displayedSpecialists
    ]
  );

  return <SpecialistContext.Provider value={value}>{children}</SpecialistContext.Provider>;
};

export const useSpecialist = (): SpecialistContextType => {
  const context = useContext(SpecialistContext);
  if (context === undefined) {
    throw new Error('useSpecialist must be used within a SpecialistProvider');
  }
  return context;
};
